import { Fragment, memo, useMemo, useRef } from "react";
import { useDroppable } from "@dnd-kit/core";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { cn, colorWithAlpha } from "@/lib/utils";
import type { Project } from "@/services/project.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import type { Columns, UiTask } from "./types";
import { DraggableTaskCard } from "./TaskCard";
import { buildSwimlanes, cellId } from "./swimlane";
import { CollapseStageButton, CollapsedStageCircle } from "./StageCollapse";

const COL_MIN = 300; // px, per stage column
const COL_COLLAPSED = 48; // px, collapsed stage column
const COL_GAP = 16; // px, matches gap-x-4

// One project row + stage column droppable cell.
function SwimlaneCell({
	id,
	projectId,
	tasks,
	projectsById,
	onDelete,
	onView,
	onAdd,
	stage,
	collapsed,
}: {
	id: string;
	projectId: string;
	stage: WorkflowStage;
	tasks: UiTask[];
	projectsById: Map<string, Project>;
	onDelete: (task: UiTask) => void;
	onView: (task: UiTask) => void;
	onAdd?: (stageKey: string, projectId?: string) => void;
	collapsed: boolean;
}) {
	const { setNodeRef, isOver } = useDroppable({ id });

	// Collapsed stage: empty drop target with task count.
	if (collapsed) {
		return (
			<div
				ref={setNodeRef}
				className={cn(
					"flex justify-center rounded-xl pt-2 min-h-[104px] text-xs font-medium text-muted transition-colors",
					isOver
						? "bg-primary-subtle/30 border border-dashed border-primary/40"
						: "bg-muted-subtle/50",
				)}
			>
				{tasks.length > 0 && tasks.length}
			</div>
		);
	}

	return (
		<div
			ref={setNodeRef}
			className={cn(
				"group/cell flex flex-col gap-2.5 rounded-xl p-2 min-h-[104px] transition-colors",
				isOver
					? "bg-primary-subtle/30 border border-dashed border-primary/40"
					: "bg-muted-subtle/50",
			)}
			style={{
				// boxShadow: `0 0 0 0.3px ${colorWithAlpha(stage.color, 0.4)} inset`,
				boxShadow: `0 0 0 300px ${colorWithAlpha(stage.color, 0.04)} inset`,
				// background: colorWithAlpha(stage.color, 0.1),
			}}
		>
			{tasks.map((task) => (
				<DraggableTaskCard
					key={task.id}
					task={task}
					projectsById={projectsById}
					onDelete={onDelete}
					onView={onView}
				/>
			))}
			{onAdd && !isOver && (
				<button
					type="button"
					onClick={() => onAdd(stage.key, projectId)}
					aria-label={`Add task to ${stage.name}`}
					className="flex-1 min-h-10 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-transparent text-xs text-muted opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100 hover:border-border hover:bg-surface/60 hover:text-foreground"
				>
					<Plus className="h-3.5 w-3.5" /> Add task
				</button>
			)}
		</div>
	);
}

function SwimlaneBoardBase({
	stages,
	stageKeys,
	filteredColumns,
	projectsById,
	collapsedProjects,
	onToggleProject,
	collapsedStages,
	onToggleStage,
	onDelete,
	onView,
	onAdd,
	boardRef,
}: {
	stages: WorkflowStage[];
	stageKeys: string[];
	filteredColumns: Columns;
	projectsById: Map<string, Project>;
	collapsedProjects: Record<string, boolean>;
	onToggleProject: (projectId: string) => void;
	collapsedStages: Record<string, boolean>;
	onToggleStage: (stageKey: string) => void;
	onDelete: (task: UiTask) => void;
	onView: (task: UiTask) => void;
	onAdd?: (stageKey: string, projectId?: string) => void;
	boardRef: React.Ref<HTMLDivElement>;
}) {
	const { projectIds, cells } = useMemo(
		() => buildSwimlanes(filteredColumns, stageKeys, projectsById),
		[filteredColumns, stageKeys, projectsById],
	);

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: stages
			.map((s) =>
				collapsedStages[s.key]
					? `${COL_COLLAPSED}px`
					: `minmax(${COL_MIN}px, 1fr)`,
			)
			.join(" "),
		minWidth:
			stages.reduce(
				(w, s) => w + (collapsedStages[s.key] ? COL_COLLAPSED : COL_MIN),
				0,
			) +
			(stages.length - 1) * COL_GAP,
	};

	// Header row lives outside the horizontal scroller so it can stick to the
	// window (a scroll container would trap position:sticky). Its scrollLeft is
	// mirrored from the board.
	const headerRef = useRef<HTMLDivElement>(null);
	const syncHeader = (e: React.UIEvent<HTMLDivElement>) => {
		if (headerRef.current)
			headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
	};

	return (
		<div>
			<div
				ref={headerRef}
				className="sticky top-14 z-20 overflow-hidden bg-background py-2"
			>
				<div className="grid gap-x-4" style={gridStyle}>
				{stages.map((stage) =>
					collapsedStages[stage.key] ? (
						<div key={stage.key} className="flex items-center py-0.5">
							<CollapsedStageCircle
								stage={stage}
								count={filteredColumns[stage.key]?.length ?? 0}
								onExpand={() => onToggleStage(stage.key)}
							/>
						</div>
					) : (
					<div
						key={stage.key}
						className="flex items-center gap-2 px-3 py-2 bg-surface rounded-xl"
						style={{
							// boxShadow: `0 0 2px 0 ${colorWithAlpha(stage.color, 0.4)} inset`,
							// Tint over opaque bg-surface so cards don't show through
							// while the header is stuck.
							backgroundImage: `linear-gradient(${colorWithAlpha(stage.color, 0.1)}, ${colorWithAlpha(stage.color, 0.1)})`,
						}}
					>
						<span
							className="h-2 w-2 rounded-full animate-pulse"
							style={{ backgroundColor: stage.color }}
						/>
						<span className="text-sm font-semibold text-foreground">
							{stage.name}
						</span>
						<span className="ml-auto text-xs font-medium text-muted bg-muted-subtle px-2 py-0.5 rounded-full">
							{filteredColumns[stage.key]?.length ?? 0}
						</span>
						<CollapseStageButton
							stage={stage}
							onClick={() => onToggleStage(stage.key)}
						/>
					</div>
					),
				)}
				</div>
			</div>

		<div ref={boardRef} onScroll={syncHeader} className="overflow-x-auto pb-4">
			<div className="grid gap-x-4 gap-y-1.5 pb-2" style={gridStyle}>
				{/* One collapsible swimlane per project */}
				{projectIds.map((projectId) => {
					const collapsed = !!collapsedProjects[projectId];
					const name = projectsById.get(projectId)?.name ?? "—";
					return (
						<Fragment key={projectId}>
							<button
								type="button"
								onClick={() => onToggleProject(projectId)}
								aria-expanded={!collapsed}
								style={{
									gridColumn: "1 / -1",
								}}
								className="sticky left-0 flex rounded-lg items-center gap-2 px-2 py-2 my-2.5 bg-surface hover:bg-muted-subtle transition-colors text-left duration-300"
							>
								<div className="flex items-center gap-2 sticky left-4">
									{collapsed ? (
										<ChevronRight className="h-4 w-4 shrink-0 text-muted" />
									) : (
										<ChevronDown className="h-4 w-4 shrink-0 text-muted" />
									)}
									<span className="text-sm font-semibold text-foreground truncate">
										{name}
									</span>
								</div>
								<span className="ml-auto sticky right-4 flex items-center gap-1 flex-wrap">
									{stages.map((stage) => {
										const count =
											cells[cellId(projectId, stage.key)]
												?.length ?? 0;
										if (count === 0) return null;
										return (
											<span
												key={stage.key}
												className="flex items-center gap-1 text-xs font-medium text-muted bg-muted-subtle px-1.5 py-1 rounded-full"
												style={{
													color: stage.color,
													backgroundColor:
														colorWithAlpha(
															stage.color,
															0.05,
														),
												}}
											>
												{/* <span
													className="h-1.5 w-1.5 rounded-full shrink-0"
													style={{
														backgroundColor:
															stage.color,
													}}
												/> */}
												{stage.name}:{" "}
												<span
													className="px-1.5 rounded-full"
													style={{
														backgroundColor:
															colorWithAlpha(
																stage.color,
																0.1,
															),
													}}
												>
													{count}
												</span>
											</span>
										);
									})}
								</span>
							</button>

							{!collapsed &&
								stages.map((stage) => {
									const id = cellId(projectId, stage.key);
									return (
										<SwimlaneCell
											key={id}
											id={id}
											projectId={projectId}
											tasks={cells[id] ?? []}
											projectsById={projectsById}
											onDelete={onDelete}
											onView={onView}
											onAdd={onAdd}
											stage={stage}
											collapsed={
												!!collapsedStages[stage.key]
											}
										/>
									);
								})}
						</Fragment>
					);
				})}

				{projectIds.length === 0 && (
					<div
						style={{ gridColumn: "1 / -1" }}
						className="flex items-center justify-center py-16"
					>
						<p className="text-sm text-muted">No tasks</p>
					</div>
				)}
			</div>
		</div>
		</div>
	);
}

export const SwimlaneBoard = memo(SwimlaneBoardBase);
